// src/hooks/useLogQueries.ts
import type {
  ApiProcessResponseType,
  ProcessFiltersType,
  ProcessStatusType,
  ProcessTypeResponse,
} from "@/types/entities/process";
import axios from "@/utils/axios";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";

export interface LogFilters {
  relationship?: string;
  entity_id?: string;
  entity?: string;
  [key: string]: string | undefined;
}

export interface LogData {
  id: number;
  description: string;
  causer_id: string;
  causer_type: string;
  subject_type: string;
  subject_id: string;
  properties: Record<string, unknown>;
}

/** one log */
export interface ApiLogSingleResponseType {
  data: LogData;
}

/** log list */
export interface ApiLogListResponseType {
  data: LogData[];
}

type LogQueryOptions = {
  filters?: LogFilters;
  params?: Record<string, string | number | boolean>;
};

type ProcessWithPublishedFlowResponse = {
  data: {
    id: string;
    name: string;
    published_flow: {
      id: string;
      version_number: number;
      status: string;
      review_status: string;
    } | null;
  };
};

export const useGetLogs = (options?: LogQueryOptions) => {
  return useQuery<ApiLogListResponseType>({
    queryKey: ["admin-log-list", options],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};

      // filters
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params[`filter[${key}]`] = value;
          }
        });
      }

      // params
      if (options?.params) {
        Object.assign(params, options.params);
      }

      if (!params.per_page) {
        params.per_page = 100;
      }

      const { data } = await axios.get("admin/v1/log", {
        params,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      return data;
    },
  });
};

export const useGetLog = (
  id: number | string | undefined,
  filters?: LogFilters,
) => {
  return useQuery({
    queryKey: ["admin-log", id, filters],
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ApiLogSingleResponseType> => {
      const response = await axios.get(`admin/v1/log/${id}`, {
        params: filters,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
  });
};

// CreateProcessPayload interface for creating a new process
export interface CreateProcessPayload {
  name: string;
  description?: string;
  max_flows?: number;
  status?: ProcessStatusType;
}

export type CreateProcessDetailsPayload = {
  description?: string;
  portal_description?: string;
  status: "PUBLISHED" | "INACTIVE" | "ON_CONSTRUCTION";
  estimated_cost_min: number;
  estimated_cost_max: number;
  estimated_time_min: number;
  estimated_time_max: number;
  estimated_time_unit: string;
  estimated_steps_min: number;
  estimated_steps_max: number;
  required_documents: Array<{
    name: string;
    description?: string;
  }>;
};

export const createProcess = async (payload: CreateProcessPayload) => {
  const { data } = await axios.post("admin/v1/process", payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
  return data;
};

export const useCreateProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-process"],
    mutationFn: createProcess,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const useCreateProcessDetails = (id: string | undefined) => {
  return useMutation({
    mutationFn: async (payload: CreateProcessDetailsPayload) => {
      const response = await axios.put(`/admin/v1/process/${id}`, payload);
      return response.data;
    },
  });
};

// Servicio para subir un archivo asociado a un proceso (sin respuesta esperada)
export interface UploadFilePayload {
  file: File;
  entity_id: string;
  entity_type: string;
  collection?: string;
}

export const uploadFile = async ({
  file,
  entity_id,
  entity_type,
  collection,
}: UploadFilePayload): Promise<void> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("entity_id", entity_id);
  formData.append("entity_type", entity_type);
  if (collection) {
    formData.append("collection", collection);
  }
  await axios.post("admin/v1/file", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      Accept: "application/json",
    },
  });
};

export const useGetProcesses = (filters?: ProcessFiltersType) => {
  return useQuery<ApiProcessResponseType>({
    queryKey: ["process-list", filters],
    queryFn: async () => {
      const { data } = await axios.get<ApiProcessResponseType>(
        "/admin/v1/process",
        {
          params: {
            ...filters,
            relationships: filters?.relationships?.join(","),
          },
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return data;
    },
  });
};

const getProcess = async (id: string) => {
  const { data } = await axios.get<ProcessTypeResponse>(
    `admin/v1/process/${id}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      params: { relationships: "createdBy,tags,files,flows" },
    },
  );
  return data;
};

const getProcessWithPublishedFlow = async (id: string) => {
  const { data } = await axios.get<ProcessWithPublishedFlowResponse>(
    `admin/v1/process/${id}`,
    {
      params: { relationships: "publishedFlow" },
    },
  );
  return data;
};

export const useGetProcessWithPublishedFlow = (id?: string) => {
  return useQuery({
    queryKey: ["admin-process-published-flow", id],
    queryFn: () => getProcessWithPublishedFlow(id as string),
    enabled: Boolean(id),
  });
};

export const createProcessDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
  type?: string | null,
) => {
  return async () => {
    const processId = id;
    try {
      const queryKey = type
        ? ["admin-process-detail", processId, type]
        : ["admin-process-detail", processId];

      const process = await queryClient.ensureQueryData({
        queryKey,
        queryFn: async () => getProcess(processId),
        staleTime: 5 * 60 * 1000,
      });

      return { data: process.data };
    } catch (error) {
      console.log("Error fetching admin-process-detail for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useGetProcess = (id?: string | number) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["admin-process-detail", id],
    queryFn: () => getProcess(id as string),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (query.isSuccess) {
      queryClient.invalidateQueries({ queryKey: ["admin-log-list"] });
    }
  }, [query.isSuccess, queryClient]);

  return query;
};

export interface UpdateProcessPayload {
  name?: string;
  description?: string;
  max_flows?: number;
  status?: ProcessStatusType;
  approved?: boolean;
}

export interface UpdateProcessResponse {
  data: {
    id: string | null;
    name: string;
    description: string;
    status: ProcessStatusType;
    max_flows: number;
    created_by: string;
    updated_at: string | null;
    created_at: string | null;
  };
}

export const updateProcess = async (
  payload: UpdateProcessPayload,
  id: string,
): Promise<UpdateProcessResponse> => {
  const { data } = await axios.patch<UpdateProcessResponse>(
    `admin/v1/process/${id}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return data;
};

export const useUpdateProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-process"],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateProcessPayload;
    }) => updateProcess(payload, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-process-detail", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const useDuplicateProcess = () => {
  return useMutation({
    mutationKey: ["duplicate-process"],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload?: UpdateProcessPayload;
    }) => {
      const response = await axios.patch(`/admin/v1/duplicate/${id}`, payload);
      return response.data;
    },
  });
};

// Interfaz para asignar tag a proceso
export interface AssignTagToProcessPayload {
  tag_id: string;
  entity: string;
  entity_id: string;
}

// Función para asignar tag a un proceso
export const assignTagToProcess = async (
  payload: AssignTagToProcessPayload,
): Promise<void> => {
  await axios.put("/admin/v1/tag/assignation/assign", payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
};

export const unassignTagToProcess = async (
  payload: AssignTagToProcessPayload,
): Promise<void> => {
  await axios.put("/admin/v1/tag/assignation/unassign", payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
};

// Hook para asignar tag a un proceso
export const useAssignTagToProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["assign-tag-to-process"],
    mutationFn: assignTagToProcess,
    onSuccess: () => {
      // Invalidar queries relacionadas con procesos cuando se asigna un tag
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const useUnassignTagToProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["unassign-tag-to-process"],
    mutationFn: unassignTagToProcess,
    onSuccess: () => {
      // Invalidar queries relacionadas con procesos cuando se desasigna un tag
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

export const useDownloadLogs = (options: LogQueryOptions) => {
  return useMutation({
    mutationKey: ["logs-download"],
    mutationFn: async () => {
      // Generate filename based on entity type
      const entity = options?.filters?.entity || "logs";
      const entityId = options?.filters?.entity_id || "";
      const filename = entity === "tag" ? `tag-logs-${entityId}` : "logs";

      const params: Record<string, string | number | boolean> = {};

      // filters
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params[`filter[${key}]`] = value;
          }
        });
      }

      // params
      if (options?.params) {
        Object.assign(params, options.params);
      }

      if (!params.per_page) {
        params.per_page = 100;
      }

      const { data } = await axios.post(
        "/admin/v1/log/download",
        { filename },
        {
          responseType: "blob",
          headers: {
            Accept: "text/csv",
            "Content-Type": "application/json",
          },
          params,
        },
      );

      const blob = new Blob([data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        filename.endsWith(".csv") ? filename : `${filename}.csv`,
      );
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
};

export const updateStatusProcess = async (
  payload: UpdateProcessPayload,
  id: string,
): Promise<UpdateProcessResponse> => {
  const { data } = await axios.post<UpdateProcessResponse>(
    `admin/v1/status/${id}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return data;
};

export interface UpdateStatusProcessPayload {
  status: "ON_CONSTRUCTION" | "PUBLISHED" | "INACTIVE";
  reason?: string;
  inactive_from?: string | Date;
  inactive_to?: string | Date;
}

export const useUpdateStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-status-process"],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateStatusProcessPayload;
    }) => updateStatusProcess(payload, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-process-detail", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};

// Nueva función para archivar/desarchivar usando PATCH
export const archiveProcess = async (
  payload: UpdateStatusProcessPayload,
  id: string,
): Promise<UpdateProcessResponse> => {
  const { data } = await axios.patch<UpdateProcessResponse>(
    `admin/v1/process/${id}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return data;
};

export const useArchiveProcess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["archive-process"],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateStatusProcessPayload;
    }) => archiveProcess(payload, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-process-detail", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["process"] });
    },
  });
};
